import { useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import qs from "qs"
import { brandRoutes } from "~/ui-rtk/constants/routes/brand-routes"
import {
  type CommentPlatform,
  useGetAdReactionsQuery,
  useListAdSummariesQuery,
  useRefreshAdSummaryMutation,
} from "~/ui-rtk/api/commentModerationApi"
import { useGetAdCreativeAnalysisQuery } from "~/ui-rtk/api/paidMediaApi"
import MarathonImage from "~/ui-rtk/components/atoms/MarathonImage/MarathonImage"
import { MarkdownContent } from "~/ui-rtk/components/chatbot/MarkdownContent"
import Button from "~/ui-rtk/components/ui/controls/Button"
import AdSummaryCard from "~/ui-rtk/pages/CommentModeration/AdSummaryCard"
import DecisionsTable from "~/ui-rtk/pages/CommentModeration/DecisionsTable"
import {
  Legend,
  QuoteList,
  ScoreBadge,
  StanceBar,
  platformLabel,
  stanceTitle,
} from "~/ui-rtk/pages/CommentModeration/reactions"

/**
 * Paid Media Optimization, Ads widget, filtered to this ad id. The filters
 * param is a qs-encoded filter list that the page's URL state hook parses;
 * it travels inside the query string, hence the second encoding.
 */
function paidOptimizationLink(adId: string): string {
  const filters = qs.stringify(
    [{ id: "ad_id", variant: "text", value: [adId], operator: "contains" }],
    { arrayFormat: "indices", allowEmptyArrays: true },
  )
  return `${brandRoutes.BRAND_MEDIA.PAID_OPTIMIZATION}?widget=ad&brand_media_filters=${encodeURIComponent(filters)}`
}

/** Drawer props must stay serializable (they live in redux). */
export interface AdCommentsDrawerProps {
  /** Meta ad id, or the report's `organic:<postId>` row key for an organic post. */
  adId: string
  adName?: string
  /** An organic post (no ad runs on it): no ad creative, summary or Paid
   *  Media link; comments are listed by `postId`. */
  organic?: boolean
  postId?: string
  platform: CommentPlatform
  permalinkUrl?: string
  /** The report's window and platform filter, so the drawer shows the same slice. */
  from: string
  to?: string
  filterPlatform?: CommentPlatform
}

/**
 * One ad's audience read: the stance bar and score, the AI "what the audience
 * says" card (refreshable), three quotes per stance, and every judged comment
 * on the posts the ad runs on with hide / unhide per row.
 */
export default function AdCommentsDrawer({
  adId,
  adName,
  organic = false,
  postId,
  platform,
  permalinkUrl,
  from,
  to,
  filterPlatform,
}: AdCommentsDrawerProps) {
  const { data: reactions } = useGetAdReactionsQuery({
    from,
    to,
    platform: filterPlatform,
  })
  const ad = reactions?.ads.find(a => a.adId === adId)
  const { data: summaries } = useListAdSummariesQuery(undefined, {
    skip: organic,
  })
  const summary = summaries?.find(s => s.adId === adId)
  const [refreshSummary] = useRefreshAdSummaryMutation()
  const [refreshing, setRefreshing] = useState(false)

  // The creative analysis saved on the ad's Asset-library row, if someone
  // (or the bulk analyze job) kept one. Null when the ad was never analysed.
  // An organic post has no ad to look up.
  const { data: analysisData } = useGetAdCreativeAnalysisQuery(
    { platform: "meta", adId },
    { skip: organic },
  )
  const analysis = analysisData?.savedAnalysis ?? null
  const [analysisOpen, setAnalysisOpen] = useState(false)
  // The report ends with a "Suggested Tags" block meant for the tag editor;
  // here only the read itself matters.
  const analysisMarkdown = useMemo(() => {
    if (!analysis) return ""
    const idx = analysis.analysis.indexOf("**Suggested Tags**")
    return idx === -1
      ? analysis.analysis
      : analysis.analysis.slice(0, idx).trimEnd()
  }, [analysis])
  let analysisKind = "Image analysis"
  if (analysis?.mediaType === "video") analysisKind = "Video analysis"
  else if (analysis?.mediaType === "text") analysisKind = "Ad metadata analysis"

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await refreshSummary({ adId, from, to }).unwrap()
      toast.success("Summary written from the comments in this window.")
    } catch (err) {
      const message = (err as { data?: { message?: string } })?.data?.message
      toast.error(message || "Could not write the summary.")
    } finally {
      setRefreshing(false)
    }
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-system-md text-white">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold">
            {organic ? "Organic post" : (adName ?? `Ad ${adId}`)}
          </h3>
          <p className="text-sm text-system-gray-400">
            {organic ? (
              <span className="font-mono text-system-gray-300">{postId}</span>
            ) : (
              <Link
                to={paidOptimizationLink(adId)}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-system-gray-300 underline hover:text-white"
                title="Open this ad in Paid Media Optimization (new tab)"
              >
                {adId}
              </Link>
            )}
            <button
              type="button"
              className="ml-1 text-xs text-system-gray-500 hover:text-white"
              title={organic ? "Copy the post id" : "Copy the ad id"}
              onClick={() => {
                void navigator.clipboard.writeText(
                  organic ? (postId ?? "") : adId,
                )
                toast.success(organic ? "Post id copied" : "Ad id copied")
              }}
            >
              copy
            </button>
            {" · "}
            {platformLabel(platform)}
            {ad
              ? ` · ${ad.comments} comment${ad.comments === 1 ? "" : "s"}${
                  ad.hidden ? ` · ${ad.hidden} hidden` : ""
                }`
              : ""}
            {permalinkUrl && (
              <>
                {" · "}
                <a
                  href={permalinkUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline hover:text-white"
                >
                  open post
                </a>
              </>
            )}
          </p>
        </div>
        {ad && <ScoreBadge score={ad.score} />}
      </div>

      {ad && (
        <div className="flex flex-col gap-2">
          <StanceBar counts={ad} title={stanceTitle(ad)} />
          <Legend />
        </div>
      )}

      {!organic && (
        <div className="flex flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue p-3">
          <MarathonImage
            sourceType="FACEBOOK_AD"
            externalRefId={adId}
            useThumbnail={false}
            alt={adName ?? adId}
            className="h-64 w-full"
          />
          {analysis && (
            <div className="flex flex-col gap-2 border-t border-basic-blue pt-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <span className="text-sm font-medium">
                    {analysisKind} · AI
                  </span>
                  {analysis.analyzedAt && (
                    <span className="ml-2 text-xs text-system-gray-500">
                      {new Date(analysis.analyzedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <Button
                  size="xs"
                  variant={{ variant: "text" }}
                  onClick={() => setAnalysisOpen(o => !o)}
                >
                  {analysisOpen ? "Hide" : "Show"}
                </Button>
              </div>
              {analysisOpen && (
                <div className="text-sm">
                  <MarkdownContent>{analysisMarkdown}</MarkdownContent>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* The AI "what the audience says" card is written per ad id; an
          organic post has none. */}
      {!organic && (
        <AdSummaryCard
          summary={summary}
          onRefresh={() => void handleRefresh()}
          refreshing={refreshing}
        />
      )}

      {ad &&
        (ad.quotes.positive.length > 0 ||
          ad.quotes.question.length > 0 ||
          ad.quotes.negative.length > 0) && (
          <div className="flex flex-col gap-3 rounded-lg border border-basic-blue bg-basic-dark-blue px-3 py-3">
            <QuoteList
              label="Positive"
              quotes={ad.quotes.positive}
              cls="text-emerald-300"
            />
            <QuoteList
              label="Questions"
              quotes={ad.quotes.question}
              cls="text-sky-300"
            />
            <QuoteList
              label="Negative"
              quotes={ad.quotes.negative}
              cls="text-rose-300"
            />
          </div>
        )}

      <div className="flex flex-col gap-system-xs">
        <h4 className="text-base font-medium">Comments</h4>
        <DecisionsTable
          // Remount on ad switch so the table's page resets — the drawer
          // instance is reused when another ad row is clicked while open.
          key={adId}
          from={from}
          to={to}
          platform={filterPlatform}
          adId={organic ? undefined : adId}
          postId={organic ? postId : undefined}
        />
      </div>
    </div>
  )
}
